<script setup>
import { inject, computed } from 'vue';
import ClassComparisonBarChart from '@/components/result/ai-literacy-2026/ClassComparisonBarChart.vue';

import icon01 from '@/assets/images/result/ai/icon_01.svg';
import icon02 from '@/assets/images/result/ai/icon_02.svg';
import icon03 from '@/assets/images/result/ai/icon_03.svg';
import icon04 from '@/assets/images/result/ai/icon_04.svg';

// 부모 컴포넌트에서 전달받은 데이터
const principalData = inject('principalData');

// 반별 색상 배열 (1반부터 순서대로)
const classColors = [
  '#062181', // 1반
  '#5953FF', // 2반
  '#27BBDE', // 3반
  '#85A8DA', // 4반
  '#FA97D2', // 5반
  '#71E0CD', // 6반
  '#C2CF5C', // 7반
  '#24A750', // 8반
  '#CF945C', // 9반
  '#FF7C3F', // 10반
  '#E53F79', // 11반
  '#B63FE5'  // 12반
];

// 반 번호에 따른 색상 반환 함수
const getClassColor = (classIndex) => {
  return classColors[classIndex] || classColors[classIndex % classColors.length];
};

// 정답률(%)을 수준으로 변환. 2025에는 원점수를 넘겨 항상 1수준이 되던 버그가 있었다.
const getLevel = (rate) => {
  if (rate >= 80) return 4;
  if (rate >= 60) return 3;
  if (rate >= 40) return 2;
  return 1;
};

// 영역별 비교 차트 데이터 계산
const comparisonSections = computed(() => {
  if (!principalData.value || !principalData.value.classList) {
    // 데이터가 없을 때 기본 구조 반환
    return [
      { title: 'AI·디지털 기술', icon: icon01, classData: [] },
      { title: 'AI·디지털 활용', icon: icon02, classData: [] },
      { title: 'AI·디지털 윤리', icon: icon03, classData: [] },
      { title: '인간중심 AI 설계', icon: icon04, classData: [] }
    ];
  }

  const classList = principalData.value.classList;
  
  return [
    {
      title: 'AI·디지털 기술',
      icon: icon01,
      classData: classList.map((classItem, index) => ({
        className: classItem.class_nm || `${classItem.school_cls}반`,
        value: Math.round((classItem.field1_rate || 0) * 10) / 10,
        level: getLevel(Number(classItem.field1_rate) || 0),
        color: getClassColor(index)
      }))
    },
    {
      title: 'AI·디지털 활용',
      icon: icon02,
      classData: classList.map((classItem, index) => ({
        className: classItem.class_nm || `${classItem.school_cls}반`,
        value: Math.round((classItem.field2_rate || 0) * 10) / 10,
        level: getLevel(Number(classItem.field2_rate) || 0),
        color: getClassColor(index)
      }))
    },
    {
      title: 'AI·디지털 윤리',
      icon: icon03,
      classData: classList.map((classItem, index) => ({
        className: classItem.class_nm || `${classItem.school_cls}반`,
        value: Math.round((classItem.field3_rate || 0) * 10) / 10,
        level: getLevel(Number(classItem.field3_rate) || 0),
        color: getClassColor(index)
      }))
    },
    {
      title: '인간중심 AI 설계',
      icon: icon04,
      classData: classList.map((classItem, index) => ({
        className: classItem.class_nm || `${classItem.school_cls}반`,
        value: Math.round((classItem.field4_rate || 0) * 10) / 10,
        level: getLevel(Number(classItem.field4_rate) || 0),
        color: getClassColor(index)
      }))
    }
  ];
});
</script>


<template>
  <div class="report-inner">
    <div class="card-wrap">
        <h5 class="card-title">학급별 학생 영역 평균 정답률 비교</h5>
        
        <div class="chart-wrap">   
          <ClassComparisonBarChart 
          v-for="(section, idx) in comparisonSections" 
          :key="idx"
          :title="section.title"
          :icon-img="section.icon"
          :class-data="section.classData"          
          />
        </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.chart-legend {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: .5rem 0;  
  .legend-item {
    display: flex;
    align-items: center;
    font-size: 1rem;
    color: var(--result-ai-primary-800);
    font-weight: 600;    
    .dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      margin-right: .5rem;
    }
  }
}
.chart-wrap {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
</style>